import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import useNotification from "src/hooks/notification";
import { getScanFields } from "src/utils/assetUtils";
import CircularProgress from "@mui/material/CircularProgress";

export default function ScanOrEnterModal({
  asset,
  markAsPicked,
  openScanModal,
  enableSuccessNotification = true,
  onClose,
}) {
  const [loading, setLoading] = useState(false);
  const [manualInput, setManualInput] = useState("");
  const { notifyError, notifySuccess } = useNotification();

  useEffect(() => {
    if (!asset) {
      notifyError("Unable to load asset information. Please try again.");
      onClose();
    }
  }, [asset, notifyError, onClose]);

  if (!asset) {
    return null;
  }

  const scanFields = getScanFields(asset);

  const validateInput = input => {
    if (!input.trim()) {
      notifyError("Please enter a value");
      return false;
    }

    const scanField = scanFields.find(([value]) => value === input.trim());
    if (scanField) {
      const [value, label] = scanField;
      if (enableSuccessNotification) {
        notifySuccess(`Successfully picked ${label}: ${value}`);
      }
      return true;
    } else {
      notifyError("Entered value does not match any asset identifiers");
      return false;
    }
  };

  const handleManualSubmit = async () => {
    setLoading(true);
    const isValid = validateInput(manualInput);

    if (isValid) {
      await markAsPicked(true);
      onClose();
    }
    setLoading(false);
  };

  const handleKeyPress = event => {
    if (event.key === "Enter" && !loading) {
      handleManualSubmit();
    }
  };

  const handleOpenScanModal = () => {
    openScanModal();
    onClose();
  };

  return (
    <Dialog open onClose={onClose} maxWidth={false} sx={{ '& .MuiDialog-paper': { width: 400 } }}>
      <DialogTitle>
        <IconButton
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
          }}
        >
          <CloseIcon />
        </IconButton>
        <Typography align="center" fontWeight={500} fontSize={22} color="text.primary">
          Scan or Enter Value
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} pt={2}>
          <TextField
            fullWidth
            variant="outlined"
            value={manualInput}
            onChange={e => setManualInput(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={loading}
            autoFocus
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={handleOpenScanModal}
          color="primary"
          variant="outlined"
          disabled={loading}
          startIcon={<QrCodeScannerIcon />}
        >
          Scan a Barcode
        </Button>
        <Button
          onClick={handleManualSubmit}
          color="primary"
          variant="contained"
          startIcon={loading && <CircularProgress size={22} />}
          disabled={loading || !manualInput.trim()}
        >
          Submit
        </Button>
      </DialogActions>
    </Dialog>
  );
}
